import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { CatalogItem, CatalogSong, ReleaseKitSummary } from '../../../../../redux/api/fymAPI.types';
import { TypedMemo } from '../../../../common/types';
import { songPageMessages } from './songPageMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the song page; each part uses its own slice of it */
import styles from './SongPage.module.scss';
/* eslint-enable css-modules/no-unused-class */

export type SongChipTone = 'ok' | 'run' | 'warn' | 'err' | 'off';

export interface SongChip {
  key: string;
  tone: SongChipTone;
  label: string;
}

const TONE_CLASS: Record<SongChipTone, string | undefined> = {
  ok: styles.ok,
  run: styles.run,
  warn: styles.warn,
  err: styles.err,
  off: styles.off,
};

/** One status pill of the song page (design song.css `.st`). */
export const SongStatusPill: React.FC<{ tone: SongChipTone; label: string; className?: string }> = ({
  tone,
  label,
  className,
}) => (
  <span className={clsx(styles.chip, TONE_CLASS[tone], className)}>
    <i />
    {label}
  </span>
);

interface SongPageChipsProps {
  item: CatalogItem;
  song: CatalogSong | undefined;
  kit: ReleaseKitSummary | undefined;
}

/**
 * What is ready for the song (design b1): the master, the lyric sync, the times to check, the
 * pre-save or SmartLink, and the kit. The chips read the catalog row first, so they show before
 * the song read ends.
 */
const SongPageChipsComponent: React.FC<SongPageChipsProps> = ({ item, song, kit }) => {
  const intl = useIntl();
  const chips = useMemo(() => {
    const list: SongChip[] = [];
    const status = song?.master?.status ?? item.song.masterStatus;
    if (status === 'ready') {
      list.push({ key: 'master', tone: 'ok', label: intl.formatMessage(songPageMessages.chipMasterReady) });
    } else if (status === 'processing') {
      list.push({ key: 'master', tone: 'run', label: intl.formatMessage(songPageMessages.chipMasterReading) });
    } else if (status === 'failed') {
      list.push({ key: 'master', tone: 'err', label: intl.formatMessage(songPageMessages.chipMasterFailed) });
    } else {
      list.push({ key: 'master', tone: 'off', label: intl.formatMessage(songPageMessages.chipNoMaster) });
    }
    const sync = song?.lyricTiming?.status ?? item.song.lyricSyncStatus;
    if (sync === 'synced') {
      list.push({ key: 'lyrics', tone: 'ok', label: intl.formatMessage(songPageMessages.chipLyricsSynced) });
    } else if (sync === 'syncing') {
      list.push({ key: 'lyrics', tone: 'run', label: intl.formatMessage(songPageMessages.chipLyricsSyncing) });
    } else if (sync === 'failed') {
      list.push({ key: 'lyrics', tone: 'err', label: intl.formatMessage(songPageMessages.chipLyricsFailed) });
    }
    const checkCount = sync === 'synced' ? (song?.lyricTiming?.checkCount ?? 0) : 0;
    if (checkCount > 0) {
      list.push({
        key: 'check',
        tone: 'warn',
        label: intl.formatMessage(songPageMessages.chipToCheck, { count: checkCount }),
      });
    }
    if (item.presaveCampaignUuid !== null) {
      list.push({ key: 'link', tone: 'ok', label: intl.formatMessage(songPageMessages.chipPresave) });
    } else if (item.smartLinkStatus === 'published') {
      list.push({ key: 'link', tone: 'ok', label: intl.formatMessage(songPageMessages.chipSmartLink) });
    }
    if (kit !== undefined && kit.totalCount > 0) {
      list.push({
        key: 'kit',
        tone: kit.workingCount > 0 ? 'run' : 'ok',
        label: intl.formatMessage(songPageMessages.chipKit, { ready: kit.readyCount, total: kit.totalCount }),
      });
    }
    return list;
  }, [intl, item, kit, song]);

  return (
    <div className={styles.chips} data-testid="song-page-chips">
      {chips.map((chip) => (
        <SongStatusPill key={chip.key} tone={chip.tone} label={chip.label} />
      ))}
    </div>
  );
};

export const SongPageChips = TypedMemo(SongPageChipsComponent);
